import type { ReportProgressData, ReportProgressStep } from "@/lib/chat-types";
import { resolveFundComplianceGatewayBaseUrl } from "@/lib/fund-compliance/gateway";
import { readString, resolveArray, resolveString, toRecord } from "@/lib/fund-compliance/parse-primitives";

/* ── Field resolution keys ───────────────────────────────────────── */

const STEP_LABEL_KEYS = ["label", "name", "title", "message"] as const;
const STEPS_ARRAY_KEYS = ["steps", "items"] as const;
const DOWNLOAD_URL_KEYS = ["downloadUrl", "url", "href"] as const;
const FILENAME_KEYS = ["filename", "name"] as const;
const FILESIZE_KEYS = ["filesize", "sizeLabel"] as const;
const BYTE_SIZE_KEYS = ["sizeBytes", "size", "bytes"] as const;
const STORAGE_NAME_KEYS = ["storageName", "storageKey", "blobName", "reportName"] as const;

/* ── Step status mapping ─────────────────────────────────────────── */

function normalizeStepStatus(raw: string | undefined): ReportProgressStep["status"] {
  const lower = raw?.toLowerCase();
  if (lower === "done" || lower === "completed" || lower === "success") return "done";
  if (lower === "active" || lower === "running" || lower === "processing") return "active";
  return "pending";
}

function formatFileSize(sizeInBytes: number) {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) return "0 B";
  if (sizeInBytes < 1024) return `${sizeInBytes} B`;
  if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
}

function resolveFilesize(record: Record<string, unknown>, fallback?: string): string {
  const label = resolveString(record, FILESIZE_KEYS);
  if (label) return label;

  for (const key of BYTE_SIZE_KEYS) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return formatFileSize(value);
    }
  }

  return fallback ?? "Preparing";
}

function resolveStorageDownloadUrl(record: Record<string, unknown>, fallback?: string): string | undefined {
  const directUrl = resolveString(record, DOWNLOAD_URL_KEYS);
  if (directUrl) return directUrl;

  const storageName = resolveString(record, STORAGE_NAME_KEYS);
  if (!storageName) return fallback;

  return `${resolveFundComplianceGatewayBaseUrl()}/api/storage/v1/files/blob/${encodeURIComponent(storageName)}`;
}

/* ── Public API ──────────────────────────────────────────────────── */

export function buildProgressData(payload: unknown, current?: ReportProgressData): ReportProgressData {
  const record = toRecord(payload);
  const stepsPayload = resolveArray(record ?? {}, STEPS_ARRAY_KEYS) ?? [];

  const steps: ReportProgressStep[] = stepsPayload
    .map((step) => {
      const stepRecord = toRecord(step);
      if (!stepRecord) return null;
      const label = resolveString(stepRecord, STEP_LABEL_KEYS);
      if (!label) return null;
      const result: ReportProgressStep = {
        label,
        status: normalizeStepStatus(readString(stepRecord.status)),
        detail: readString(stepRecord.detail),
      };
      return result;
    })
    .filter((s): s is ReportProgressStep => s !== null);

  const fileRecord = toRecord(record?.report) ?? toRecord(record?.file);

  return {
    title:
      resolveString(record ?? {}, ["title", "message"]) ??
      current?.title ??
      "Compliance Review Progress",
    steps: steps.length > 0 ? steps : current?.steps ?? [],
    report: fileRecord
      ? {
          filename: resolveString(fileRecord, FILENAME_KEYS) ?? current?.report?.filename ?? "Compliance report.pdf",
          filesize: resolveFilesize(fileRecord, current?.report?.filesize),
          status:
            readString(fileRecord.status)?.toLowerCase() === "ready"
              ? "ready"
              : current?.report?.status ?? "generating",
          downloadUrl: resolveStorageDownloadUrl(fileRecord, current?.report?.downloadUrl),
          storageName: resolveString(fileRecord, STORAGE_NAME_KEYS) ?? current?.report?.storageName,
        }
      : current?.report,
  };
}

export function buildReportReadyData(payload: unknown, current?: ReportProgressData): ReportProgressData {
  const record = toRecord(payload);
  return {
    title: current?.title ?? "Compliance Review Progress",
    steps: current?.steps?.map((step) => ({ ...step, status: "done" as const })) ?? [],
    report: {
      filename:
        resolveString(record ?? {}, FILENAME_KEYS) ??
        current?.report?.filename ??
        "Compliance report.pdf",
      filesize:
        resolveFilesize(record ?? {}, current?.report?.filesize ?? "Ready"),
      status: "ready",
      downloadUrl: resolveStorageDownloadUrl(record ?? {}, current?.report?.downloadUrl),
      storageName: resolveString(record ?? {}, STORAGE_NAME_KEYS) ?? current?.report?.storageName,
    },
  };
}

export function buildHistoryReportData(payload: unknown): ReportProgressData | null {
  const record = toRecord(payload);
  if (!record) return null;

  const filename =
    resolveString(record, [...FILENAME_KEYS, "storageName", "title"]) ??
    "Compliance report.pdf";

  return {
    title: "Compliance Review Pipeline",
    steps: [],
    pct: 100,
    pctMessage: "Review complete",
    report: {
      filename,
      filesize: resolveFilesize(record, "Ready"),
      status: "ready",
      downloadUrl: resolveStorageDownloadUrl(record),
      storageName: resolveString(record, STORAGE_NAME_KEYS),
    },
  };
}
